export enum LogType {
    Log,
    Warning,
    Error,
    LogData,
    Write,
}

type LogData = {
    [key: string]: any;
};

export type ReceiverFunc = (type: LogType, data: any[]) => void;

interface IReceiver {
    receiver: ReceiverFunc;
    id: number;
}

class ConsoleLogger {
    private IDs = 0;
    private readonly receivers: IReceiver[] = [];
    private readonly messages: {logType: LogType; data: any[];}[] = [];

    public addReceiver(receiver: ReceiverFunc): number {
        const id = this.IDs++;

        this.receivers.push({
            id, receiver
        });

        this.messages.forEach(({logType, data}) => {
            receiver(logType, data);
        });

        return id;
    }

    public removeReceiver(id: number): boolean {
        const index = this.receivers.findIndex(item => item.id === id);

        if (index === -1) {
            return false;
        }

        this.receivers.splice(index, 1);

        return true;
    }

    public removeAllReceivers(): void {
        this.receivers.length = 0;
    }

    /**
     * Only will write to console, can be used for development and debug purposes. Won't save logs for error reporting.
     */
    public write(...message: any[]): this {
        return this.send(LogType.Write, message);
    }

    public log(...message: any[]): this {
        return this.send(LogType.Log, message);
    }

    public warn(...message: any[]): this {
        return this.send(LogType.Warning, message);
    }

    public error(...message: any[]): this {
        return this.send(LogType.Error, message);
    }

    public logData(message: string, data?: LogData): this {
        return this.send(LogType.LogData, [message, data]);
    }

    public stringifyObjects(data: any[]): string {
        return data
            .map(item => {
                if (typeof item === 'object') {
                    try {
                        item = JSON.stringify(item);
                    } catch (error) {
                        item = item.toString();
                    }
                }

                return item;
            })
            .join('•');
    }

    public clear(): void {
        this.messages.length = 0;
    }

    private send(logType: LogType, data: any[]): this {
        this.messages.push({logType, data});
        this.receivers
            .forEach(receiver => receiver.receiver(logType, data));

        return this;
    }
}

export const consoleLogger = new ConsoleLogger();
